import React, { useEffect, useState } from 'react';
import '../styles/CarritoDropdown.css';
import { API_URL } from '../config';

type Producto = {
  nombre: string;
  sku?: string;
  imagen: string;
  color: string;
  cantidad: number;
  precio: number;
};

interface Props {
  visible: boolean;
  onClose: () => void;
}

const CarritoDropdown: React.FC<Props> = ({ visible, onClose }) => {
  const [carrito, setCarrito] = useState<Producto[]>([]);
  const [stock, setStock] = useState<Record<string, number>>({});

  const guardarCarrito = (nuevo: Producto[]) => {
    localStorage.setItem('carrito', JSON.stringify(nuevo));
    setCarrito(nuevo);
    (window as any).actualizarCantidadCarrito?.();
  };

  const cargarCarrito = () => {
    const items = JSON.parse(localStorage.getItem('carrito') || '[]');
    setCarrito(items);
  };

  useEffect(() => {
    cargarCarrito();
  }, []);

  useEffect(() => {
  const onChange = () => cargarCarrito();
  window.addEventListener('carrito:change', onChange);

  const onStorage = (e: StorageEvent) => { if (e.key === 'carrito') cargarCarrito(); };
  window.addEventListener('storage', onStorage);

  // compatibilidad con tu hook global
  const w = window as any;
  const prev = w.actualizarCantidadCarrito;
  w.actualizarCantidadCarrito = () => { try { onChange(); } finally { if (typeof prev === 'function') prev(); } };

  return () => {
    window.removeEventListener('carrito:change', onChange);
    window.removeEventListener('storage', onStorage);
    w.actualizarCantidadCarrito = prev;
  };
}, []);

  // Cargar stock desde backend (interno, no se muestra)
  useEffect(() => {
    const fetchStock = async () => {
      if (carrito.length === 0) return;
      try {
        const sku = carrito[0]?.sku || 'PREM-SHAKER-650';
        const res = await fetch(`${API_URL}/api/products/${sku}/availability`);
        const data = await res.json();
        setStock(data.stock || {});
      } catch {
        console.error('Error al cargar stock');
      }
    };
    fetchStock();
  }, [carrito]);

  // Validar stock al cambiar cantidad (solo interno)
  const cambiarCantidad = (index: number, delta: number) => {
    const copia = [...carrito];
    const item = copia[index];
    const disponible = stock[item.color] ?? Infinity;

    if (delta > 0 && item.cantidad + 1 > disponible) {
      alert('No hay stock suficiente para este color.');
      return;
    }

    item.cantidad += delta;
    if (item.cantidad <= 0) copia.splice(index, 1);
    guardarCarrito(copia);
  };

  const eliminar = (index: number) => {
    const copia = [...carrito];
    copia.splice(index, 1);
    guardarCarrito(copia);
  };

  const total = carrito.reduce(
    (acc, item) => acc + (item.precio || 0) * (item.cantidad || 1),
    0
  );

  if (!visible) return null;

  return (
    <div className="carrito-dropdown">
      <h4>Carrito de Compras</h4>

      {carrito.length === 0 ? (
        <p>Tu carrito está vacío.</p>
      ) : (
        <>
          <ul className="carrito-lista">
            {carrito.map((item, index) => (
              <li key={index} className="carrito-item">
                <img src={item.imagen} alt={item.nombre} />
                <div>
                  <p>
                    {item.nombre} {item.color ? `(${item.color})` : ''}
                  </p>
                  <p>Precio: ${item.precio?.toLocaleString()}</p>
                  <div className="cantidad-controls">
                    <button onClick={() => cambiarCantidad(index, -1)}>-</button>
                    <span>{item.cantidad}</span>
                    <button onClick={() => cambiarCantidad(index, 1)}>+</button>
                    <button onClick={() => eliminar(index)}>Eliminar</button>
                  </div>
                  {/* Eliminado: visualización del stock */}
                </div>
              </li>
            ))}
          </ul>

          <div className="carrito-total">
            <strong>Total: ${total.toLocaleString()}</strong>
          </div>

          <div className="carrito-acciones-dropdown">
            <button className="btn-secundario" onClick={onClose}>
              Seguir comprando
            </button>
            <button
              className="btn-principal"
              onClick={() => (window.location.href = '/carrito')}
            >
              Ir al carrito
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CarritoDropdown;
